import React, { useEffect, useMemo, useRef, useState, } from "react";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Box, Icon, Spinner, useFormControl } from "@chakra-ui/react";
import { useCombobox, useMultipleSelection } from "downshift";
import { RemoveScroll } from "react-remove-scroll";
import isEqual from "lodash/isEqual";
import { useVirtualizer } from "@tanstack/react-virtual";
import { SelectOption } from "./select-option";
import { Popover, usePopover } from "./controlled-popover";
// Used to identify a tag input option for creating a new option, e.g. "Create 'something'"
const CreatableOption = Symbol("creatable option");
const defaultIsOptionDisabled = () => false;
function defaultOptionLabel(option) {
    return String(option.label ?? "");
}
function defaultOptionValue(option) {
    return option.value;
}
export function TagInput({ isDisabled = false, isLoading = false, isInvalid = false, isOptionDisabled = defaultIsOptionDisabled, supportsCreatableOptions, options, optionLabel = defaultOptionLabel, optionValue = defaultOptionValue, optionAccessory, placeholder = "", value, width = "xs", onChange, onCreateOption, }) {
    const [isCreating, setIsCreating] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const { getDropdownProps, getSelectedItemProps, addSelectedItem, removeSelectedItem, } = useMultipleSelection({
        selectedItems: value,
        onStateChange({ selectedItems: newSelectedItems, type }) {
            switch (type) {
                case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
                case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
                case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
                case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
                    onChange(newSelectedItems ?? []);
                    break;
                default:
                    break;
            }
        },
        onSelectedItemsChange({ selectedItems }) {
            onChange(selectedItems ?? []);
        },
    });
    const filteredOptions = useMemo(() => {
        const regex = new RegExp(inputValue, "i");
        return options.filter((option) => {
            // Exclude all options that are already selected
            const isSelected = value.some((valueItem) => {
                return isEqual(optionValue(option), valueItem);
            });
            if (isSelected) {
                return false;
            }
            if (inputValue.length === 0) {
                return true;
            }
            return regex.test(optionLabel(option));
        });
    }, [options, optionLabel, optionValue, inputValue, value]);
    const tagInputOptions = useMemo(() => {
        return isCreating ? [CreatableOption] : filteredOptions;
    }, [isCreating, filteredOptions]);
    const formControl = useFormControl({
        isDisabled: isDisabled || isLoading,
        isInvalid,
    });
    const { isOpen, highlightedIndex, getComboboxProps, getInputProps, getMenuProps, getItemProps, getToggleButtonProps, } = useCombobox({
        labelId: `${formControl.id}-label`,
        inputId: formControl.id,
        items: tagInputOptions,
        itemToString(option) {
            if (!option) {
                return "";
            }
            if (typeof option === "symbol") {
                return inputValue;
            }
            return optionLabel(option);
        },
        defaultHighlightedIndex: 0,
        selectedItem: null,
        inputValue,
        onInputValueChange(changes) {
            setInputValue(changes.inputValue ?? "");
        },
        onStateChange({ type, selectedItem }) {
            if (type === useCombobox.stateChangeTypes.InputKeyDownEnter ||
                type === useCombobox.stateChangeTypes.ItemClick) {
                if (!selectedItem) {
                    return;
                }
                if (typeof selectedItem === "symbol") {
                    if (typeof onCreateOption === "function") {
                        setIsCreating(false);
                        setInputValue("");
                        void onCreateOption(inputValue ?? "");
                    }
                    return;
                }
                addSelectedItem(optionValue(selectedItem));
            }
        },
    });
    useEffect(() => {
        if (supportsCreatableOptions &&
            inputValue.length > 0 &&
            filteredOptions.length === 0) {
            setIsCreating(true);
        }
    }, [supportsCreatableOptions, inputValue, filteredOptions]);
    useEffect(() => {
        if (filteredOptions.length > 0) {
            setIsCreating(false);
        }
    }, [filteredOptions]);
    // Reset input value to empty string, if user hasn't selected any option and input lost focus
    useEffect(() => {
        if (isOpen) {
            return;
        }
        setInputValue("");
    }, [isOpen]);
    const selectedOptions = useMemo(() => {
        const result = [];
        for (const valueItem of value) {
            const option = options.find((option) => {
                return isEqual(optionValue(option), valueItem);
            });
            if (option) {
                result.push(option);
            }
        }
        return result;
    }, [options, optionValue, value]);
    const { getTargetProps, getPopoverProps } = usePopover();
    const showMenu = isOpen && tagInputOptions.length > 0;
    let cursor;
    if (isLoading) {
        cursor = "wait";
    }
    if (isDisabled) {
        cursor = "not-allowed";
    }
    const optionsContainer = useRef(null);
    const virtualizer = useVirtualizer({
        count: tagInputOptions.length,
        getItemKey(index) {
            const option = tagInputOptions[index];
            if (typeof option === "symbol") {
                return "creatable";
            }
            return option ? optionLabel(option) : index;
        },
        getScrollElement() {
            return optionsContainer.current;
        },
        estimateSize() {
            return 37;
        },
    });
    useEffect(() => {
        virtualizer.scrollToIndex(highlightedIndex);
    }, [highlightedIndex]);
    return (React.createElement(RemoveScroll, { enabled: isOpen },
        React.createElement(Box, { display: "flex", alignItems: "center", gap: 3, border: "1px", borderColor: isInvalid ? "red.500" : "gray.400", borderRadius: "md", transition: "200ms all", width: width, maxWidth: "100%", minHeight: "36px", cursor: cursor, opacity: isDisabled ? 0.4 : undefined, _hover: isDisabled
                ? undefined
                : {
                    borderColor: isInvalid ? "red.600" : "gray.500",
                }, _focusWithin: {
                boxShadow: "outlineGreen",
            }, ...getTargetProps() },
            React.createElement(Box, { flex: "1", minWidth: 0, display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 2, rowGap: 1, p: 1.5 },
                selectedOptions.map((selectedOption, index) => (React.createElement(Box, { key: optionLabel(selectedOption), bg: "avocado", color: "forest", px: 2, height: 6, borderRadius: "base", display: "flex", alignItems: "center", gap: 2, outline: "none", _focus: {
                        bg: "lightspeed",
                    }, ...(isDisabled
                        ? {}
                        : getSelectedItemProps({
                            selectedItem: optionValue(selectedOption),
                            index,
                        })) },
                    React.createElement(Box, { as: "span", fontSize: "md" }, optionLabel(selectedOption)),
                    React.createElement(Box, { "aria-hidden": true, as: "span", display: "flex", alignItems: "center", outline: "none", minWidth: 0, height: "auto", onClick: (event) => {
                            event.stopPropagation();
                            if (isDisabled) {
                                return;
                            }
                            removeSelectedItem(optionValue(selectedOption));
                        } },
                        React.createElement(Icon, { as: XMarkIcon, boxSize: 3 }))))),
                React.createElement(Box, { flex: "1", minWidth: "96px", ml: -1, ...getComboboxProps(), display: selectedOptions.length === options.length &&
                        !supportsCreatableOptions
                        ? "none"
                        : undefined },
                    React.createElement(Box, { as: "input", "aria-invalid": formControl["aria-invalid"], "aria-required": formControl["aria-required"], "aria-describedby": formControl["aria-describedby"], fontSize: "md", width: "100%", height: "100%", placeholder: value.length === 0 ? placeholder : undefined, outline: "none", pl: 2, pr: 3, disabled: isDisabled, cursor: cursor, ...getInputProps(getDropdownProps({
                            preventKeyAction: isOpen,
                            onFocus: formControl.onFocus,
                            onBlur: formControl.onBlur,
                        })) }))),
            React.createElement(Box, { flex: "none", pr: 3, display: "flex", alignItems: "center" }, isLoading ? (React.createElement(Spinner, { color: "forest", emptyColor: "gray.200", size: "sm", speed: "440ms", thickness: "2px" })) : (React.createElement(Box, { as: "button", type: "button", height: "100%", display: "flex", alignItems: "center", disabled: isDisabled, cursor: cursor, ...getToggleButtonProps() },
                React.createElement(Icon, { as: ChevronDownIcon, boxSize: 4 }))))),
        React.createElement(Popover, { isOpen: showMenu, ...getMenuProps(getPopoverProps()) },
            React.createElement(Box, { ref: optionsContainer, height: "100%", overflowY: "auto" },
                React.createElement(Box, { position: "relative", height: virtualizer.getTotalSize() }, virtualizer.getVirtualItems().map((virtualItem) => {
                    const option = tagInputOptions[virtualItem.index];
                    let isSelected = false;
                    let isDisabled = false;
                    let label = "";
                    let accessory;
                    if (typeof option === "symbol") {
                        isSelected = true;
                        label = `Create "${inputValue}"`;
                    }
                    else {
                        isDisabled = isOptionDisabled(option);
                        label = optionLabel(option);
                        if (typeof optionAccessory === "function") {
                            accessory = optionAccessory(option);
                        }
                    }
                    return (React.createElement(Box, { key: virtualItem.key, position: "absolute", top: 0, left: 0, width: "100%", height: virtualItem.size, transform: `translateY(${virtualItem.start}px)` },
                        React.createElement(SelectOption, { key: label, isFocused: virtualItem.index === highlightedIndex, isSelected: isSelected, isDisabled: isDisabled, label: label, accessory: accessory, itemProps: getItemProps({
                                item: option,
                                index: virtualItem.index,
                                disabled: isDisabled,
                            }) })));
                }))))));
}
